import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
    Box,
    Button,
    Container,
    Typography,
    Divider,
    TextField,
    Menu,
    Checkbox,
    FormControlLabel,
    FormGroup,
    Autocomplete,
    Chip,
    InputAdornment,
    CircularProgress,
    Paper,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    IconButton,
} from "@mui/material";
import { ExpandMore as ExpandMoreIcon } from "@mui/icons-material";

import NotebooksTable from "./NotebooksTable";
import { useAuth } from "../../../helpers/AuthProvider";
import queryGraphql from "../../../helpers/ApiInterface";
import MainDrawer from "../../MainDrawer";
import MiniBot from "../../MiniBot";

// Define types for owner, approver, and application details
interface NotebookParticipantInfo {
    id: string;
    name: string;
    googleProfilePicture?: string;
}

interface NotebookApplicationInfo {
    id: string;
    applicationName: string;
}

interface NotebookSpecificDetails {
    region?: string;
    zone?: string;
    workbenchType?: string;
    jupyterLabVersion?: string;
    machineType?: string;
    network?: string;
    subNetwork?: string;
}

// Main Notebook interface
interface Notebook {
    id: string;
    name: string;
    application?: NotebookApplicationInfo;
    cloudProvider?: string;
    costLabelPairs?: { cost: number; label: string }[];
    status: 'pending' | 'active' | 'rejected' | string;
    details?: {
        notebooks?: NotebookSpecificDetails;
    };
    reasonForRejection?: string;
    createdAt?: string;
    updatedAt?: string;
    solutionOwner?: NotebookParticipantInfo | null;
    solutionApprover?: NotebookParticipantInfo | null;
}

interface SearchOptions {
    chipLabel: string;
    dropdownLabel: string;
    category: keyof Notebook | "solutionOwner.name" | "details.notebooks.region" | "details.notebooks.machineType" | "details.notebooks.workbenchType" | "application.applicationName" | string;
    groupBy: string;
}

const Notebooks: React.FC = () => {
    const [allNotebooks, setAllNotebooks] = useState<Notebook[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const { user } = useAuth();
    const navigate = useNavigate();
    const [searchQuery, setSearchQuery] = useState<SearchOptions[]>([]);
    const [columnFilterAnchorEl, setColumnFilterAnchorEl] = useState<null | HTMLElement>(null);
    const [searchOptionsMasterList, setSearchOptionsMasterList] = useState<SearchOptions[]>([]);


    const initialColumnFilters: Record<string, boolean> = {
        name: true,
        cloudProvider: true,
        application: true,
        region: true,
        machineType: true,
        workbenchType: true,
        solutionOwner: true,
        createdAt: true,
    };
    const [columnFilters, setColumnFilters] = useState<Record<string, boolean>>(initialColumnFilters);
    const [tempColumnFilters, setTempColumnFilters] = useState<Record<string, boolean>>(initialColumnFilters);

    useEffect(() => {
        const fetchData = async () => {
            setIsLoading(true);
            try {
                const response = await queryGraphql(`
                    query GetNotebooks {
                      getNotebooks {
                        id
                        name
                        application {
                           id
                           applicationName
                        }
                        cloudProvider
                        costLabelPairs {
                          cost
                          label
                        }
                        status
                        details {
                          notebooks {
                            region
                            zone
                            workbenchType
                            jupyterLabVersion
                            machineType
                            network
                            subNetwork
                          }
                        }
                        reasonForRejection
                        createdAt
                        updatedAt
                        solutionOwner {
                          id
                          name
                          googleProfilePicture
                        }
                        solutionApprover {
                          id
                          name
                          googleProfilePicture
                        }
                      }
                    }
                `);

                const notebooksData = response.data?.getNotebooks || response.getNotebooks || [];

                if (notebooksData) {
                    const processedNotebooks = notebooksData.map((nb: any): Notebook => ({
                        id: nb.id,
                        name: nb.name || "Unnamed Notebook",
                        application: nb.application ? {
                            id: nb.application.id,
                            applicationName: nb.application.applicationName,
                        } : undefined,
                        cloudProvider: nb.cloudProvider,
                        costLabelPairs: nb.costLabelPairs,
                        status: (nb.status || 'pending').toLowerCase(),
                        details: nb.details,
                        reasonForRejection: nb.reasonForRejection,
                        createdAt: nb.createdAt,
                        updatedAt: nb.updatedAt,
                        solutionOwner: nb.solutionOwner ? {
                            id: nb.solutionOwner.id,
                            name: nb.solutionOwner.name,
                            googleProfilePicture: nb.solutionOwner.googleProfilePicture,
                        } : null,
                        solutionApprover: nb.solutionApprover ? {
                            id: nb.solutionApprover.id,
                            name: nb.solutionApprover.name,
                        } : null,
                    }));
                    setAllNotebooks(processedNotebooks);

                    let dynamicSearchOptions: SearchOptions[] = processedNotebooks
                        .map((pn: Notebook) => [
                            { category: "name", groupBy: "NOTEBOOK NAME", chipLabel: `Name: ${pn.name}`, dropdownLabel: pn.name },
                            ...(pn.cloudProvider ? [{ category: "cloudProvider", groupBy: "CLOUD PROVIDER", chipLabel: `Provider: ${pn.cloudProvider}`, dropdownLabel: pn.cloudProvider }] : []),
                            ...(pn.application ? [{ category: "application.applicationName" as any, groupBy: "APPLICATION", chipLabel: `App: ${pn.application.applicationName}`, dropdownLabel: pn.application.applicationName }] : []),
                            { category: "status", groupBy: "STATUS", chipLabel: `Status: ${pn.status}`, dropdownLabel: pn.status.charAt(0).toUpperCase() + pn.status.slice(1) },
                            ...(pn.details?.notebooks?.region ? [{ category: "details.notebooks.region" as any, groupBy: "REGION", chipLabel: `Region: ${pn.details.notebooks.region}`, dropdownLabel: pn.details.notebooks.region }] : []),
                            ...(pn.details?.notebooks?.machineType ? [{ category: "details.notebooks.machineType" as any, groupBy: "MACHINE TYPE", chipLabel: `Machine: ${pn.details.notebooks.machineType}`, dropdownLabel: pn.details.notebooks.machineType }] : []),
                            ...(pn.details?.notebooks?.workbenchType ? [{ category: "details.notebooks.workbenchType" as any, groupBy: "WORKBENCH TYPE", chipLabel: `Workbench: ${pn.details.notebooks.workbenchType}`, dropdownLabel: pn.details.notebooks.workbenchType }] : []),
                            ...(pn.solutionOwner ? [{ category: "solutionOwner.name" as any, groupBy: "OWNER", chipLabel: `Owner: ${pn.solutionOwner.name}`, dropdownLabel: pn.solutionOwner.name }] : []),
                        ])
                        .flat();

                    dynamicSearchOptions = dynamicSearchOptions.filter(
                        (obj, index, self) =>
                            index === self.findIndex((t) =>
                                t.category === obj.category && t.dropdownLabel === obj.dropdownLabel
                            )
                    );
                    dynamicSearchOptions.sort((a, b) => {
                        const groupA = a.groupBy.toUpperCase();
                        const groupB = b.groupBy.toUpperCase();
                        if (groupA > groupB) return 1;
                        if (groupA < groupB) return -1;
                        const labelA = String(a.dropdownLabel).toUpperCase();
                        const labelB = String(b.dropdownLabel).toUpperCase();
                        if (labelA > labelB) return 1;
                        if (labelA < labelB) return -1;
                        return 0;
                    });
                    setSearchOptionsMasterList(dynamicSearchOptions);
                }
            } catch (error) {
                console.error("Error fetching Notebooks:", error);
            } finally {
                setIsLoading(false);
            }
        };
        fetchData();
    }, []);

    const handleColumnFilterClick = (event: React.MouseEvent<HTMLElement>) => {
        setTempColumnFilters(columnFilters);
        setColumnFilterAnchorEl(event.currentTarget);
    };

    const handleColumnFilterClose = () => {
        setColumnFilterAnchorEl(null);
    };

    const handleTempColumnFilterChange = (column: string) => {
        setTempColumnFilters((prevFilters) => ({
            ...prevFilters,
            [column]: !prevFilters[column],
        }));
    };

    const handleApplyColumnFilters = () => {
        setColumnFilters(tempColumnFilters);
        handleColumnFilterClose();
    };

    const getDisplayLabelForKey = (key: string): string => {
        if (key === 'name') return 'Notebook Name';
        if (key === 'cloudProvider') return 'Cloud Provider';
        if (key === 'application') return 'Application';
        if (key === 'region') return 'Region';
        if (key === 'machineType') return 'Machine Type';
        if (key === 'workbenchType') return 'Workbench Type';
        if (key === 'solutionOwner') return 'Solution Owner';
        if (key === 'createdAt') return 'Added On';
        
        const result = key.replace(/([A-Z])/g, ' $1');
        return result.charAt(0).toUpperCase() + result.slice(1).trim();
    };


    const globallyFilteredNotebooks =
        searchQuery.length === 0
            ? allNotebooks
            : allNotebooks.filter((notebook: Notebook) => {
                return searchQuery.every(queryParam => {
                    let notebookValue: any;
                    if (queryParam.category === "application.applicationName") {
                        notebookValue = notebook.application?.applicationName;
                    } else if (queryParam.category === "solutionOwner.name") {
                        notebookValue = notebook.solutionOwner?.name;
                    } else if (queryParam.category === "details.notebooks.region") {
                        notebookValue = notebook.details?.notebooks?.region;
                    } else if (queryParam.category === "details.notebooks.machineType") {
                        notebookValue = notebook.details?.notebooks?.machineType;
                    } else if (queryParam.category === "details.notebooks.workbenchType") {
                        notebookValue = notebook.details?.notebooks?.workbenchType;
                    } else {
                        notebookValue = notebook[queryParam.category as keyof Notebook];
                    }

                    if (notebookValue === null || notebookValue === undefined) return false;
                    return String(notebookValue).toLowerCase().includes(String(queryParam.dropdownLabel).toLowerCase());
                });
            });
    
    const pendingNotebooks = globallyFilteredNotebooks.filter(nb => nb.status === 'pending' || nb.status === 'submitted' || nb.status === 'provisioning');
    const activeNotebooks = globallyFilteredNotebooks.filter(nb => nb.status === 'active' || nb.status === 'approved' || nb.status === 'success');
    const rejectedNotebooks = globallyFilteredNotebooks.filter(nb => nb.status === 'rejected' || nb.status === 'failed');

    const totalNotebooksOverall = allNotebooks.length;
    const totalMatchingSearch = globallyFilteredNotebooks.length;

    const canAddNotebook = user.roles.includes("cluster_owner") || user.roles.includes("viewer");


    const handleAddNotebook = () => {
        navigate("/aisolutions/notebooks/add");
    };

    if (isLoading) {
        return (
            <Box sx={{ display: "flex" }}>
                <MainDrawer index={"aisolutions-notebooks"} />
                <Box component="main" sx={{ flex: 1, p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', height: 'calc(100vh - 64px)' }}>
                    <CircularProgress />
                </Box>
            </Box>
        );
    }

    return (
        <Box sx={{ display: "flex" }}>
            <MainDrawer index={"aisolutions-notebooks"} />
            <Box
                component="main"
                sx={{
                    flex: 1,
                    bgcolor: "#fafafa",
                    minHeight: "100vh",
                }}
            >
                <MiniBot />
                <Container maxWidth={false} disableGutters>
                    <Box bgcolor="#FFFFFF" p={{xs:1.5, sm:2}} mb="18px" >
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: { xs: 'column', sm: 'row' },
                                justifyContent: "space-between",
                                alignItems: { xs: 'flex-start', sm: 'center' },
                                 mx:2
                            }}
                        >
                            <Typography fontSize="22px" gutterBottom fontFamily="Gilroy-Bold" color="#1F2937">
                                Notebooks
                            </Typography>
                            <Box sx={{display:'flex', alignItems:'center'}}>
                                <Box>
                                    {canAddNotebook && (
                                        <Button
                                            variant="contained"
                                            sx={{
                                                backgroundColor: "black", color: "white", padding: "8px 24px",
                                                fontSize: "0.875rem", textTransform: "none", fontFamily: "Gilroy-Bold",
                                                borderRadius: "6px", '&:hover': { backgroundColor: '#333333' }
                                            }}
                                            onClick={handleAddNotebook}
                                            disabled={!user.roles.includes("cluster_owner") && !user.roles.includes("viewer")}
                                        >
                                            Add New Notebook
                                        </Button>
                                    )}
                                </Box>
                                <IconButton
                                    aria-label="Help" 
                                    onClick={()=> window.open(`${process.env.REACT_APP_HOST_DOMAIN}/docs` , "_blank")}
                                    sx={{
                                        padding: 0, 
                                        border: 'none',  
                                        ml:2,
                                        '&:hover': {
                                            backgroundColor: 'action.hover', 
                                        },
                                    }}
                                >
                                    <Box
                                        component="img"
                                        src="/assets/Help.svg"
                                        alt="Help" 
                                        sx={{
                                            width: 40,  
                                            height: 40,
                                            display: 'block', 
                                        }}
                                    />
                                </IconButton>
                            </Box>
                        </Box>
                        <Divider sx={{ marginY: 1 }} />
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: 'wrap' }}>
                            <Autocomplete
                                multiple
                                options={searchOptionsMasterList}
                                filterSelectedOptions
                                getOptionLabel={(option) => option.dropdownLabel}
                                value={searchQuery}
                                groupBy={(option) => option.groupBy}
                                renderTags={(tagValue, getTagProps) =>
                                    tagValue.map((option, index) => (
                                        <Chip {...getTagProps({ index })} size="small" label={option.chipLabel} sx={{ fontFamily: "Gilroy-Regular" }} />
                                    ))
                                }
                                onChange={(e, newVal) => {
                                    setSearchQuery(newVal as SearchOptions[]);
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        variant="filled"
                                        placeholder="Search by notebook name, type, status, etc."
                                        fullWidth
                                        size="small"
                                        hiddenLabel
                                        sx={{ minWidth: { xs: '100%', sm: 300, md: 500 }, flexGrow: 1 }}
                                        InputProps={{
                                            ...params.InputProps,
                                            startAdornment: (
                                                <>
                                                    <InputAdornment position="start" sx={{ pl: 0.5 }}>
                                                        <img src="/assets/search-normal.svg" alt="Search Icon" style={{ width: "16px" }} />
                                                    </InputAdornment>
                                                    {params.InputProps.startAdornment}
                                                </>
                                            ),
                                            disableUnderline: true,
                                            style: { backgroundColor: "#f4f4f4", minHeight: "44px", borderRadius: "8px", fontFamily: "Gilroy-Regular", fontSize: "14px" },
                                        }}
                                    />
                                )}
                                PaperComponent={(props) => <Paper elevation={3} {...props} />}
                            />
                            <Button
                                                            sx={{
                                                                border: "1px solid #bbb",
                                                                borderRadius: "10px",
                                                                height: "45px",
                                                                width: "45px",
                                                            }}
                                                            onClick={handleColumnFilterClick}
                                                        >
                                                            <img src="/assets/pause.svg" alt="Filter Icon" style={{ width: "16px" }} />
                                                        </Button>
                            <Menu
                                anchorEl={columnFilterAnchorEl}
                                open={Boolean(columnFilterAnchorEl)}
                                onClose={handleColumnFilterClose}
                                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                                transformOrigin={{ vertical: "top", horizontal: "right" }}
                                PaperProps={{ sx: { border: '1px solid #E0E0E0', borderRadius: '8px', boxShadow: '0px 4px 12px rgba(0,0,0,0.1)', mt: 1 } }}
                            >
                                <Box sx={{ p: "16px", minWidth: 240, maxWidth: 280 }}>
                                    <Typography sx={{ fontFamily: "Gilroy-Bold", fontSize: "16px", fontWeight: 700, color: "#1F2937", marginBottom: "16px" }}>
                                        TABLE COLUMNS
                                    </Typography>
                                    <FormGroup>
                                        {Object.entries(tempColumnFilters).map(([key, value]) => (
                                            <FormControlLabel
                                                key={key}
                                                control={
                                                    <Checkbox
                                                        checked={value}
                                                        onChange={() => handleTempColumnFilterChange(key)}
                                                        size="small"
                                                        sx={{ color: "#4B5563", "&.Mui-checked": { color: "#000000" }, "& .MuiSvgIcon-root": { fontSize: "22px" }, py: '4px', mr: '8px' }}
                                                    />
                                                }
                                                label={<Typography sx={{ fontFamily: "Gilroy-Medium", fontSize: '17px', color: '#1F2937', lineHeight: '1.5', py: '1vh' }}>{getDisplayLabelForKey(key)}</Typography>}
                                                sx={{ marginBottom: '4px' }}
                                            />
                                        ))}
                                    </FormGroup>
                                    <Button
                                        variant="contained" fullWidth onClick={handleApplyColumnFilters}
                                        sx={{ fontFamily: "Gilroy-Bold", fontSize: "14px", fontWeight: 700, color: "#FFFFFF", backgroundColor: "#000000", textTransform: 'uppercase', borderRadius: "6px", padding: "10px 0", marginTop: "20px", '&:hover': { backgroundColor: "#333333" } }}
                                    >
                                        Apply
                                    </Button>
                                </Box>
                            </Menu>
                        </Box>
                    </Box>

                    <Box m={{xs: 1, sm:2}}>
                        {totalNotebooksOverall > 0 && totalMatchingSearch > 0 &&(
                            <>
                        {/* Pending Accordion */}
                        <Accordion
                            defaultExpanded={pendingNotebooks.length > 0}
                            elevation={0}
                            sx={{ borderRadius: "8px", mb: "16px", "&:before": { display: "none" }, bgcolor: "#fafafa" }}
                            disabled={pendingNotebooks.length === 0 && searchQuery.length > 0}
                        >
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Box sx={{ display: "flex", alignItems: "center" }}>
                                    <img src="/assets/clock.svg" alt="Pending Icon" style={{ width: "20px", marginRight: "8px" }} />
                                    <Typography fontSize="16px" fontFamily="Gilroy-SemiBold">
                                        Pending Notebooks ({pendingNotebooks.length})
                                    </Typography>
                                </Box>
                            </AccordionSummary>
                            <AccordionDetails sx={{ p: { xs: 1, sm: 2 } }}>
                                {pendingNotebooks.length > 0 ? (
                                    <NotebooksTable
                                        notebooks={pendingNotebooks}
                                        columnFilters={columnFilters}
                                        statusType="pending"
                                    />
                                ) : (
                                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', fontFamily:"Gilroy-Regular", py:2 }}>
                                        No pending notebooks match your filters.
                                    </Typography>
                                )}
                            </AccordionDetails>
                        </Accordion>

                        {/* Active Accordion */}
                        <Accordion
                            defaultExpanded={activeNotebooks.length > 0}
                            elevation={0}
                            sx={{ borderRadius: "8px", mb: "16px", "&:before": { display: "none" }, bgcolor: "#fafafa" }}
                            disabled={activeNotebooks.length === 0 && searchQuery.length > 0}
                        >
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Box sx={{ display: "flex", alignItems: "center" }}>
                                    <img src="/assets/tickcircle.svg" alt="Active Icon" style={{ width: "20px", marginRight: "8px" }} />
                                    <Typography fontSize="16px" fontFamily="Gilroy-SemiBold">
                                        Active Notebooks ({activeNotebooks.length})
                                    </Typography>
                                </Box>
                            </AccordionSummary>
                            <AccordionDetails sx={{ p: { xs: 1, sm: 2 } }}>
                                {activeNotebooks.length > 0 ? (
                                    <NotebooksTable
                                        notebooks={activeNotebooks}
                                        columnFilters={columnFilters}
                                        statusType="active"
                                    />
                                ) : (
                                     <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', fontFamily:"Gilroy-Regular", py:2 }}>
                                        No active notebooks match your filters.
                                    </Typography>
                                )}
                            </AccordionDetails>
                        </Accordion>

                        {/* Rejected Accordion */}
                        <Accordion
                            defaultExpanded={rejectedNotebooks.length > 0}
                            elevation={0}
                            sx={{ borderRadius: "8px", "&:before": { display: "none" }, bgcolor: "#fafafa" }}
                            disabled={rejectedNotebooks.length === 0 && searchQuery.length > 0}
                        >
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Box sx={{ display: "flex", alignItems: "center" }}>
                                    <img src="/assets/reject.svg" alt="Rejected Icon" style={{ width: "20px", marginRight: "8px" }} />
                                    <Typography fontSize="16px" fontFamily="Gilroy-SemiBold">
                                        Rejected Notebooks ({rejectedNotebooks.length})
                                    </Typography>
                                </Box>
                            </AccordionSummary>
                            <AccordionDetails sx={{ p: { xs: 1, sm: 2 } }}>
                                {rejectedNotebooks.length > 0 ? (
                                    <NotebooksTable
                                        notebooks={rejectedNotebooks}
                                        columnFilters={columnFilters}
                                        statusType="rejected"
                                    />
                                ) : (
                                     <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', fontFamily:"Gilroy-Regular", py:2 }}>
                                        No rejected notebooks match your filters.
                                    </Typography>
                                )}
                            </AccordionDetails>
                        </Accordion>
                        </>)}
                         {totalNotebooksOverall > 0 && totalMatchingSearch === 0 && !isLoading && (
                            <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" sx={{ textAlign: 'center', mt: 5, p: 2, backgroundColor:"#FFFFFF", borderRadius:"8px" }}>
                                <img src="/assets/inbox-icon.svg" alt="No Notebooks Match" style={{ width: '100px', marginBottom: '20px' }} />
                                <Typography variant="h6" sx={{ mb: 1, fontFamily: "Gilroy-SemiBold", color: "#1F2937" }}>
                                    No Notebooks match your search
                                </Typography>
                                <Typography variant="body2" color="#6B7280" sx={{ mb: 3, fontFamily: "Gilroy-Regular", maxWidth: '350px' }}>
                                    Try adjusting your search terms or filters.
                                </Typography>
                            </Box>
                        )}

                        {totalNotebooksOverall === 0 && !isLoading && (
                             <Box
                                display="flex" flexDirection="column" justifyContent="center" alignItems="center"
                                sx={{
                                    textAlign: 'center', mt: { xs: 2, sm: 5 }, px: 2, py: 4,
                                    backgroundColor: '#FFFFFF', borderRadius: "8px",
                                    boxShadow: "0px 1px 3px rgba(0,0,0,0.05)"
                                }}
                            >
                                <img src="/assets/inbox-icon.svg" alt="No Notebooks" style={{ width: '100px', marginBottom: '20px' }} />
                                <Typography variant="h6" sx={{ mb: 1, fontFamily: "Gilroy-SemiBold", color: "#1F2937" }}>
                                    No Notebooks are added yet
                                </Typography>
                                <Typography variant="body2" color="#6B7280" sx={{ mb: 3, fontFamily: "Gilroy-Regular", maxWidth: '380px' }}>
                                    Click the button above to add your first Notebook.
                                </Typography>
                            </Box>
                        )}
                    </Box>
                </Container>
            </Box>
        </Box>
    );
};

export default Notebooks;




// import React, { useState, useEffect } from "react";
// import { useNavigate } from "react-router-dom";
// import {
//     Box,
//     Button,
//     Container,
//     Typography,
//     Divider,
//     TextField,
//     Menu,
//     Checkbox,
//     FormControlLabel,
//     FormGroup,
//     Autocomplete,
//     Chip,
//     InputAdornment,
//     CircularProgress,
//     Paper,
//     Accordion,
//     AccordionSummary,
//     AccordionDetails,
//     IconButton,
// } from "@mui/material";
// import { ExpandMore as ExpandMoreIcon } from "@mui/icons-material";

// import NotebooksTable from "./NotebooksTable";
// import { useAuth } from "../../../helpers/AuthProvider";
// import queryGraphql from "../../../helpers/ApiInterface";
// import MainDrawer from "../../MainDrawer";
// import MiniBot from "../../MiniBot";

// // Define types for owner, approver, and application details
// interface NotebookParticipantInfo {
//     id: string;
//     name: string;
// }

// interface NotebookApplicationInfo {
//     id: string;
//     applicationName: string;
// }

// interface NotebookSpecificDetails {
//     region?: string;
//     zone?: string;
//     workbenchType?: string;
//     jupyterLabVersion?: string;
//     machineType?: string;
//     network?: string;
//     subNetwork?: string;
// }

// // Main Notebook interface
// interface Notebook {
//     id: string;
//     name: string;
//     application?: NotebookApplicationInfo;
//     solutionType?: string;
//     costLabelPairs?: { cost: number; label: string }[];
//     status: 'pending' | 'active' | 'rejected' | string;
//     details?: {
//         notebooks?: NotebookSpecificDetails;
//     };
//     reasonForRejection?: string;
//     createdAt?: string;
//     updatedAt?: string;
//     solutionOwner?: NotebookParticipantInfo | null;
//     solutionApprover?: NotebookParticipantInfo | null;
// }

// interface SearchOptions {
//     chipLabel: string;
//     dropdownLabel: string;
//     category: keyof Notebook | "solutionOwner.name" | "details.notebooks.region" | "details.notebooks.machineType" | "details.notebooks.workbenchType" | "application.applicationName" | string;
//     groupBy: string;
// }

// const Notebooks: React.FC = () => {
//     const [allNotebooks, setAllNotebooks] = useState<Notebook[]>([]);
//     const [isLoading, setIsLoading] = useState(true);
//     const { user } = useAuth();
//     const navigate = useNavigate();
//     const [searchQuery, setSearchQuery] = useState<SearchOptions[]>([]);
//     const [columnFilterAnchorEl, setColumnFilterAnchorEl] = useState<null | HTMLElement>(null);
//     const [searchOptionsMasterList, setSearchOptionsMasterList] = useState<SearchOptions[]>([]);


//     const initialColumnFilters: Record<string, boolean> = {
//         name: true,
//         application: true,
//         solutionType: true,
//         region: true,
//         machineType: true,
//         workbenchType: true,
//         solutionOwner: true,
//         createdAt: true,
//     };
//     const [columnFilters, setColumnFilters] = useState<Record<string, boolean>>(initialColumnFilters);
//     const [tempColumnFilters, setTempColumnFilters] = useState<Record<string, boolean>>(initialColumnFilters);

//     useEffect(() => {
//         const fetchData = async () => {
//             setIsLoading(true);
//             try {
//                 const response = await queryGraphql(`
//                     query GetNotebooks {
//                       getNotebooks {
//                         id
//                         name
//                         application {
//                            id
//                            applicationName
//                         }
//                         solutionType
//                         costLabelPairs {
//                           cost
//                           label
//                         }
//                         status
//                         details {
//                           notebooks {
//                             region
//                             zone
//                             workbenchType
//                             jupyterLabVersion
//                             machineType
//                             network
//                             subNetwork
//                           }
//                         }
//                         reasonForRejection
//                         createdAt
//                         updatedAt
//                         solutionOwner {
//                           id
//                           name
//                         }
//                         solutionApprover {
//                           id
//                           name
//                         }
//                       }
//                     }
//                 `);

//                 const notebooksData = response.data?.getNotebooks || response.getNotebooks || [];

//                 if (notebooksData) {
//                     const processedNotebooks = notebooksData.map((nb: any): Notebook => ({
//                         id: nb.id,
//                         name: nb.name || "Unnamed Notebook",
//                         application: nb.application ? {
//                             id: nb.application.id,
//                             applicationName: nb.application.applicationName,
//                         } : undefined,
//                         solutionType: nb.solutionType,
//                         costLabelPairs: nb.costLabelPairs,
//                         status: (nb.status || 'pending').toLowerCase(),
//                         details: nb.details,
//                         reasonForRejection: nb.reasonForRejection,
//                         createdAt: nb.createdAt,
//                         updatedAt: nb.updatedAt,
//                         solutionOwner: nb.solutionOwner ? {
//                             id: nb.solutionOwner.id,
//                             name: nb.solutionOwner.name,
//                         } : null,
//                         solutionApprover: nb.solutionApprover ? {
//                             id: nb.solutionApprover.id,
//                             name: nb.solutionApprover.name,
//                         } : null,
//                     }));
//                     setAllNotebooks(processedNotebooks);

//                     let dynamicSearchOptions: SearchOptions[] = processedNotebooks
//                         .map((pn: Notebook) => [
//                             { category: "name", groupBy: "NOTEBOOK NAME", chipLabel: `Name: ${pn.name}`, dropdownLabel: pn.name },
//                             ...(pn.application ? [{ category: "application.applicationName" as any, groupBy: "APPLICATION", chipLabel: `App: ${pn.application.applicationName}`, dropdownLabel: pn.application.applicationName }] : []),
//                             ...(pn.solutionType ? [{ category: "solutionType", groupBy: "SOLUTION TYPE", chipLabel: `Type: ${pn.solutionType}`, dropdownLabel: pn.solutionType }] : []),
//                             { category: "status", groupBy: "STATUS", chipLabel: `Status: ${pn.status}`, dropdownLabel: pn.status.charAt(0).toUpperCase() + pn.status.slice(1) },
//                             ...(pn.details?.notebooks?.region ? [{ category: "details.notebooks.region" as any, groupBy: "REGION", chipLabel: `Region: ${pn.details.notebooks.region}`, dropdownLabel: pn.details.notebooks.region }] : []),
//                             ...(pn.details?.notebooks?.machineType ? [{ category: "details.notebooks.machineType" as any, groupBy: "MACHINE TYPE", chipLabel: `Machine: ${pn.details.notebooks.machineType}`, dropdownLabel: pn.details.notebooks.machineType }] : []),
//                             ...(pn.details?.notebooks?.workbenchType ? [{ category: "details.notebooks.workbenchType" as any, groupBy: "WORKBENCH TYPE", chipLabel: `Workbench: ${pn.details.notebooks.workbenchType}`, dropdownLabel: pn.details.notebooks.workbenchType }] : []),
//                             ...(pn.solutionOwner ? [{ category: "solutionOwner.name" as any, groupBy: "OWNER", chipLabel: `Owner: ${pn.solutionOwner.name}`, dropdownLabel: pn.solutionOwner.name }] : []),
//                         ])
//                         .flat();

//                     dynamicSearchOptions = dynamicSearchOptions.filter(
//                         (obj, index, self) =>
//                             index === self.findIndex((t) =>
//                                 t.category === obj.category && t.dropdownLabel === obj.dropdownLabel
//                             )
//                     );
//                     dynamicSearchOptions.sort((a, b) => {
//                         const groupA = a.groupBy.toUpperCase();
//                         const groupB = b.groupBy.toUpperCase();
//                         if (groupA > groupB) return 1;
//                         if (groupA < groupB) return -1;
//                         const labelA = String(a.dropdownLabel).toUpperCase();
//                         const labelB = String(b.dropdownLabel).toUpperCase();
//                         if (labelA > labelB) return 1;
//                         if (labelA < labelB) return -1;
//                         return 0;
//                     });
//                     setSearchOptionsMasterList(dynamicSearchOptions);
//                 }
//             } catch (error) {
//                 console.error("Error fetching Notebooks:", error);
//             } finally {
//                 setIsLoading(false);
//             }
//         };
//         fetchData();
//     }, []);

//     const handleColumnFilterClick = (event: React.MouseEvent<HTMLElement>) => {
//         setTempColumnFilters(columnFilters);
//         setColumnFilterAnchorEl(event.currentTarget);
//     };

//     const handleColumnFilterClose = () => {
//         setColumnFilterAnchorEl(null);
//     };

//     const handleTempColumnFilterChange = (column: string) => {
//         setTempColumnFilters((prevFilters) => ({
//             ...prevFilters,
//             [column]: !prevFilters[column],
//         }));
//     };

//     const handleApplyColumnFilters = () => {
//         setColumnFilters(tempColumnFilters);
//         handleColumnFilterClose();
//     };

//     const getDisplayLabelForKey = (key: string): string => {
//         if (key === 'name') return 'Notebook Name';
//         if (key === 'application') return 'Application';
//         if (key === 'solutionType') return 'Solution Type';
//         if (key === 'region') return 'Region';
//         if (key === 'machineType') return 'Machine Type';
//         if (key === 'workbenchType') return 'Workbench Type';
//         if (key === 'solutionOwner') return 'Solution Owner';
//         if (key === 'createdAt') return 'Added On';
        
//         const result = key.replace(/([A-Z])/g, ' $1');
//         return result.charAt(0).toUpperCase() + result.slice(1).trim();
//     };


//     const globallyFilteredNotebooks =
//         searchQuery.length === 0
//             ? allNotebooks
//             : allNotebooks.filter((notebook: Notebook) => {
//                 return searchQuery.every(queryParam => {
//                     let notebookValue: any;
//                     if (queryParam.category === "application.applicationName") {
//                         notebookValue = notebook.application?.applicationName;
//                     } else if (queryParam.category === "solutionOwner.name") {
//                         notebookValue = notebook.solutionOwner?.name;
//                     } else if (queryParam.category === "details.notebooks.region") {
//                         notebookValue = notebook.details?.notebooks?.region;
//                     } else if (queryParam.category === "details.notebooks.machineType") {
//                         notebookValue = notebook.details?.notebooks?.machineType;
//                     } else if (queryParam.category === "details.notebooks.workbenchType") {
//                         notebookValue = notebook.details?.notebooks?.workbenchType;
//                     } else {
//                         notebookValue = notebook[queryParam.category as keyof Notebook];
//                     }

//                     if (notebookValue === null || notebookValue === undefined) return false;
//                     return String(notebookValue).toLowerCase().includes(String(queryParam.dropdownLabel).toLowerCase());
//                 });
//             });
    
//     const pendingNotebooks = globallyFilteredNotebooks.filter(nb => nb.status === 'pending' || nb.status === 'submitted' || nb.status === 'provisioning');
//     const activeNotebooks = globallyFilteredNotebooks.filter(nb => nb.status === 'active' || nb.status === 'approved' || nb.status === 'success');
//     const rejectedNotebooks = globallyFilteredNotebooks.filter(nb => nb.status === 'rejected' || nb.status === 'failed');

//     const totalNotebooksOverall = allNotebooks.length;
//     const totalMatchingSearch = globallyFilteredNotebooks.length;

//     const canAddNotebook = user.roles.includes("cluster_owner") || user.roles.includes("viewer");


//     const handleAddNotebook = () => {
//         navigate("/aisolutions/notebooks/add");
//     };

//     if (isLoading) {
//         return (
//             <Box sx={{ display: "flex" }}>
//                 <MainDrawer index={"aisolutions-notebooks"} />
//                 <Box component="main" sx={{ flex: 1, p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', height: 'calc(100vh - 64px)' }}>
//                     <CircularProgress />
//                 </Box>
//             </Box>
//         );
//     }

//     return (
//         <Box sx={{ display: "flex" }}>
//             <MainDrawer index={"aisolutions-notebooks"} />
//             <Box
//                 component="main"
//                 sx={{
//                     flex: 1,
//                     bgcolor: "#fafafa",
//                     minHeight: "100vh",
//                 }}
//             >
//                 <MiniBot />
//                 <Container maxWidth={false} disableGutters>
//                     <Box bgcolor="#FFFFFF" p={{xs:1.5, sm:2}} mb="18px" >
//                         <Box
//                             sx={{
//                                 display: "flex",
//                                 flexDirection: { xs: 'column', sm: 'row' },
//                                 justifyContent: "space-between",
//                                 alignItems: { xs: 'flex-start', sm: 'center' },
//                                  mx:2
//                             }}
//                         >
//                             <Typography fontSize="22px" gutterBottom fontFamily="Gilroy-Bold" color="#1F2937">
//                                 Notebooks
//                             </Typography>
//                             <Box sx={{display:'flex', alignItems:'center'}}>
//                                 <Box>
//                                     {canAddNotebook && (
//                                         <Button
//                                             variant="contained"
//                                             sx={{
//                                                 backgroundColor: "black", color: "white", padding: "8px 24px",
//                                                 fontSize: "0.875rem", textTransform: "none", fontFamily: "Gilroy-Bold",
//                                                 borderRadius: "6px", '&:hover': { backgroundColor: '#333333' }
//                                             }}
//                                             onClick={handleAddNotebook}
//                                             disabled={!user.roles.includes("cluster_owner") && !user.roles.includes("viewer")}
//                                         >
//                                             Add New Notebook
//                                         </Button>
//                                     )}
//                                 </Box>
//                                 <IconButton
//                                     aria-label="Help" 
//                                     onClick={()=> window.open(`${process.env.REACT_APP_HOST_DOMAIN}/docs` , "_blank")}
//                                     sx={{
//                                         padding: 0, 
//                                         border: 'none',  
//                                         ml:2,
//                                         '&:hover': {
//                                             backgroundColor: 'action.hover', 
//                                         },
//                                     }}
//                                 >
//                                     <Box
//                                         component="img"
//                                         src="/assets/Help.svg"
//                                         alt="Help" 
//                                         sx={{
//                                             width: 40,  
//                                             height: 40,
//                                             display: 'block', 
//                                         }}
//                                     />
//                                 </IconButton>
//                             </Box>
//                         </Box>
//                         <Divider sx={{ marginY: 2 }} />
//                         <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: 'wrap' }}>
//                             <Autocomplete
//                                 multiple
//                                 options={searchOptionsMasterList}
//                                 filterSelectedOptions
//                                 getOptionLabel={(option) => option.dropdownLabel}
//                                 value={searchQuery}
//                                 groupBy={(option) => option.groupBy}
//                                 renderTags={(tagValue, getTagProps) =>
//                                     tagValue.map((option, index) => (
//                                         <Chip {...getTagProps({ index })} size="small" label={option.chipLabel} sx={{ fontFamily: "Gilroy-Regular" }} />
//                                     ))
//                                 }
//                                 onChange={(e, newVal) => {
//                                     setSearchQuery(newVal as SearchOptions[]);
//                                 }}
//                                 renderInput={(params) => (
//                                     <TextField
//                                         {...params}
//                                         variant="filled"
//                                         placeholder="Search by notebook name, type, status, etc."
//                                         fullWidth
//                                         size="small"
//                                         hiddenLabel
//                                         sx={{ minWidth: { xs: '100%', sm: 300, md: 500 }, flexGrow: 1 }}
//                                         InputProps={{
//                                             ...params.InputProps,
//                                             startAdornment: (
//                                                 <>
//                                                     <InputAdornment position="start" sx={{ pl: 0.5 }}>
//                                                         <img src="/assets/search-normal.svg" alt="Search Icon" style={{ width: "16px" }} />
//                                                     </InputAdornment>
//                                                     {params.InputProps.startAdornment}
//                                                 </>
//                                             ),
//                                             disableUnderline: true,
//                                             style: { backgroundColor: "#f4f4f4", minHeight: "44px", borderRadius: "8px", fontFamily: "Gilroy-Regular", fontSize: "14px" },
//                                         }}
//                                     />
//                                 )}
//                                 PaperComponent={(props) => <Paper elevation={3} {...props} />}
//                             />
//                             <Button
//                                 sx={{
//                                     border: "1px solid #bbb",
//                                     borderRadius: "10px",
//                                     height: "44px",
//                                     minWidth: "44px",
//                                     padding: "0px",
//                                     color: "#4B5563",
//                                     '&:hover': { backgroundColor: "#E5E7EB", borderColor: "#9CA3AF" }
//                                 }}
//                                 onClick={handleColumnFilterClick}
//                                 title="Filter Columns"
//                             >
//                                 <img src="/assets/pause.svg" alt="Filter Columns" style={{ width: "20px", height: "20px" }} />
//                             </Button>
//                             <Menu
//                                 anchorEl={columnFilterAnchorEl}
//                                 open={Boolean(columnFilterAnchorEl)}
//                                 onClose={handleColumnFilterClose}
//                                 anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
//                                 transformOrigin={{ vertical: "top", horizontal: "right" }}
//                                 PaperProps={{ sx: { border: '1px solid #E0E0E0', borderRadius: '8px', boxShadow: '0px 4px 12px rgba(0,0,0,0.1)', mt: 1 } }}
//                             >
//                                 <Box sx={{ p: "16px", minWidth: 240, maxWidth: 280 }}>
//                                     <Typography sx={{ fontFamily: "Gilroy-Bold", fontSize: "16px", fontWeight: 700, color: "#1F2937", marginBottom: "16px" }}>
//                                         TABLE COLUMNS
//                                     </Typography>
//                                     <FormGroup>
//                                         {Object.entries(tempColumnFilters).map(([key, value]) => (
//                                             <FormControlLabel
//                                                 key={key}
//                                                 control={
//                                                     <Checkbox
//                                                         checked={value}
//                                                         onChange={() => handleTempColumnFilterChange(key)}
//                                                         size="small"
//                                                         sx={{ color: "#4B5563", "&.Mui-checked": { color: "#000000" }, "& .MuiSvgIcon-root": { fontSize: "22px" }, py: '4px', mr: '8px' }}
//                                                     />
//                                                 }
//                                                 label={<Typography sx={{ fontFamily: "Gilroy-Medium", fontSize: '17px', color: '#1F2937', lineHeight: '1.5', py: '1vh' }}>{getDisplayLabelForKey(key)}</Typography>}
//                                                 sx={{ marginBottom: '4px' }}
//                                             />
//                                         ))}
//                                     </FormGroup>
//                                     <Button
//                                         variant="contained" fullWidth onClick={handleApplyColumnFilters}
//                                         sx={{ fontFamily: "Gilroy-Bold", fontSize: "14px", fontWeight: 700, color: "#FFFFFF", backgroundColor: "#000000", textTransform: 'uppercase', borderRadius: "6px", padding: "10px 0", marginTop: "20px", '&:hover': { backgroundColor: "#333333" } }}
//                                     >
//                                         Apply
//                                     </Button>
//                                 </Box>
//                             </Menu>
//                         </Box>
//                     </Box>

//                     <Box m={{xs: 1, sm:2}}>
//                         {totalNotebooksOverall > 0 && totalMatchingSearch > 0 &&(
//                             <>
//                         {/* Pending Accordion */}
//                         <Accordion
//                             defaultExpanded={pendingNotebooks.length > 0}
//                             elevation={0}
//                             sx={{ borderRadius: "8px", mb: "16px", "&:before": { display: "none" }, bgcolor: "#fafafa" }}
//                             disabled={pendingNotebooks.length === 0 && searchQuery.length > 0}
//                         >
//                             <AccordionSummary expandIcon={<ExpandMoreIcon />}>
//                                 <Box sx={{ display: "flex", alignItems: "center" }}>
//                                     <img src="/assets/clock.svg" alt="Pending Icon" style={{ width: "20px", marginRight: "8px" }} />
//                                     <Typography fontSize="16px" fontFamily="Gilroy-SemiBold">
//                                         Pending Notebooks ({pendingNotebooks.length})
//                                     </Typography>
//                                 </Box>
//                             </AccordionSummary>
//                             <AccordionDetails sx={{ p: { xs: 1, sm: 2 } }}>
//                                 {pendingNotebooks.length > 0 ? (
//                                     <NotebooksTable
//                                         notebooks={pendingNotebooks}
//                                         columnFilters={columnFilters}
//                                         statusType="pending"
//                                     />
//                                 ) : (
//                                     <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', fontFamily:"Gilroy-Regular", py:2 }}>
//                                         No pending notebooks match your filters.
//                                     </Typography>
//                                 )}
//                             </AccordionDetails>
//                         </Accordion>

//                         {/* Active Accordion */}
//                         <Accordion
//                             defaultExpanded={activeNotebooks.length > 0}
//                             elevation={0}
//                             sx={{ borderRadius: "8px", mb: "16px", "&:before": { display: "none" }, bgcolor: "#fafafa" }}
//                             disabled={activeNotebooks.length === 0 && searchQuery.length > 0}
//                         >
//                             <AccordionSummary expandIcon={<ExpandMoreIcon />}>
//                                 <Box sx={{ display: "flex", alignItems: "center" }}>
//                                     <img src="/assets/tickcircle.svg" alt="Active Icon" style={{ width: "20px", marginRight: "8px" }} />
//                                     <Typography fontSize="16px" fontFamily="Gilroy-SemiBold">
//                                         Active Notebooks ({activeNotebooks.length})
//                                     </Typography>
//                                 </Box>
//                             </AccordionSummary>
//                             <AccordionDetails sx={{ p: { xs: 1, sm: 2 } }}>
//                                 {activeNotebooks.length > 0 ? (
//                                     <NotebooksTable
//                                         notebooks={activeNotebooks}
//                                         columnFilters={columnFilters}
//                                         statusType="active"
//                                     />
//                                 ) : (
//                                      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', fontFamily:"Gilroy-Regular", py:2 }}>
//                                         No active notebooks match your filters.
//                                     </Typography>
//                                 )}
//                             </AccordionDetails>
//                         </Accordion>

//                         {/* Rejected Accordion */}
//                         <Accordion
//                             defaultExpanded={rejectedNotebooks.length > 0}
//                             elevation={0}
//                             sx={{ borderRadius: "8px", "&:before": { display: "none" }, bgcolor: "#fafafa" }}
//                             disabled={rejectedNotebooks.length === 0 && searchQuery.length > 0}
//                         >
//                             <AccordionSummary expandIcon={<ExpandMoreIcon />}>
//                                 <Box sx={{ display: "flex", alignItems: "center" }}>
//                                     <img src="/assets/reject.svg" alt="Rejected Icon" style={{ width: "20px", marginRight: "8px" }} />
//                                     <Typography fontSize="16px" fontFamily="Gilroy-SemiBold">
//                                         Rejected Notebooks ({rejectedNotebooks.length})
//                                     </Typography>
//                                 </Box>
//                             </AccordionSummary>
//                             <AccordionDetails sx={{ p: { xs: 1, sm: 2 } }}>
//                                 {rejectedNotebooks.length > 0 ? (
//                                     <NotebooksTable
//                                         notebooks={rejectedNotebooks}
//                                         columnFilters={columnFilters}
//                                         statusType="rejected"
//                                     />
//                                 ) : (
//                                      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', fontFamily:"Gilroy-Regular", py:2 }}>
//                                         No rejected notebooks match your filters.
//                                     </Typography>
//                                 )}
//                             </AccordionDetails>
//                         </Accordion>
//                         </>)}
//                          {totalNotebooksOverall > 0 && totalMatchingSearch === 0 && !isLoading && (
//                             <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" sx={{ textAlign: 'center', mt: 5, p: 2, backgroundColor:"#FFFFFF", borderRadius:"8px" }}>
//                                 <img src="/assets/inbox-icon.svg" alt="No Notebooks Match" style={{ width: '100px', marginBottom: '20px' }} />
//                                 <Typography variant="h6" sx={{ mb: 1, fontFamily: "Gilroy-SemiBold", color: "#1F2937" }}>
//                                     No Notebooks match your search
//                                 </Typography>
//                                 <Typography variant="body2" color="#6B7280" sx={{ mb: 3, fontFamily: "Gilroy-Regular", maxWidth: '350px' }}>
//                                     Try adjusting your search terms or filters.
//                                 </Typography>
//                             </Box>
//                         )}

//                         {totalNotebooksOverall === 0 && !isLoading && (
//                              <Box
//                                 display="flex" flexDirection="column" justifyContent="center" alignItems="center"
//                                 sx={{
//                                     textAlign: 'center', mt: { xs: 2, sm: 5 }, px: 2, py: 4,
//                                     backgroundColor: '#FFFFFF', borderRadius: "8px",
//                                     boxShadow: "0px 1px 3px rgba(0,0,0,0.05)"
//                                 }}
//                             >
//                                 <img src="/assets/inbox-icon.svg" alt="No Notebooks" style={{ width: '100px', marginBottom: '20px' }} />
//                                 <Typography variant="h6" sx={{ mb: 1, fontFamily: "Gilroy-SemiBold", color: "#1F2937" }}>
//                                     No Notebooks are added yet
//                                 </Typography>
//                                 <Typography variant="body2" color="#6B7280" sx={{ mb: 3, fontFamily: "Gilroy-Regular", maxWidth: '380px' }}>
//                                     Click the button above to add your first Notebook.
//                                 </Typography>
//                             </Box>
//                         )}
//                     </Box>
//                 </Container>
//             </Box>
//         </Box>
//     );
// };

// export default Notebooks;


// import React, { useState, useEffect } from "react";
// import { useNavigate } from "react-router-dom";
// import {
//     Box,
//     Button,
//     Container,
//     Typography,
//     Divider,
//     TextField,
//     Menu,
//     Checkbox,
//     FormControlLabel,
//     FormGroup,
//     Autocomplete,
//     Chip,
//     InputAdornment,
//     CircularProgress,
//     Paper,
//     Accordion,
//     AccordionSummary,
//     AccordionDetails,
//     IconButton,
// } from "@mui/material";
// import { ExpandMore as ExpandMoreIcon } from "@mui/icons-material";

// // Import the new NotebooksTable and its related interfaces
// import NotebooksTable from "./NotebooksTable"; // Adjusted import
// import { useAuth } from "../../../helpers/AuthProvider"; // Assuming path is correct
// import queryGraphql from "../../../helpers/ApiInterface"; // Assuming path is correct
// import MainDrawer from "../../MainDrawer"; // Assuming path is correct
// import MiniBot from "../../MiniBot"; // Assuming path is correct

// // Define types for owner and approver details, mirroring NotebooksTable
// interface NotebookParticipantInfo {
//     id: string;
//     name: string;
// }

// interface NotebookSpecificDetails {
//     region?: string;
//     zone?: string;
//     workbenchType?: string;
//     jupyterLabVersion?: string;
//     machineType?: string;
//     network?: string;
//     subNetwork?: string;
// }

// // Main Notebook interface, mirroring NotebooksTable
// interface Notebook {
//     id: string;
//     name: string;
//     solutionType?: string;
//     costLabelPairs?: { cost: number; label: string }[];
//     status: 'pending' | 'active' | 'rejected'  | string; // Ensure this covers all statuses from your API
//     details?: {
//         notebooks?: NotebookSpecificDetails;
//     };
//     reasonForRejection?: string;
//     createdAt?: string;
//     updatedAt?: string;
//     solutionOwner?: NotebookParticipantInfo | null;
//     solutionApprover?: NotebookParticipantInfo | null;
// }


// interface SearchOptions {
//     chipLabel: string;
//     dropdownLabel: string;
//     category: keyof Notebook | "solutionOwner.name" | "details.notebooks.region" | "details.notebooks.machineType" | "details.notebooks.workbenchType" | string; // Adjusted for Notebook fields
//     groupBy: string;
// }

// // searchOptionsMasterList should be component-specific if it's derived from dynamic data
// // let searchOptionsMasterList: SearchOptions[] = []; // Initialize inside component or manage via state if dynamic

// const Notebooks: React.FC = () => {
//     const [allNotebooks, setAllNotebooks] = useState<Notebook[]>([]);
//     const [isLoading, setIsLoading] = useState(true);
//     const { user } = useAuth();
//     const navigate = useNavigate();
//     const [searchQuery, setSearchQuery] = useState<SearchOptions[]>([]);
//     const [columnFilterAnchorEl, setColumnFilterAnchorEl] = useState<null | HTMLElement>(null);
//     const [searchOptionsMasterList, setSearchOptionsMasterList] = useState<SearchOptions[]>([]);


//     const initialColumnFilters: Record<string, boolean> = {
//         name: true,
//         solutionType: true,
//         region: true, // From details.notebooks.region
//         machineType: true, // From details.notebooks.machineType
//         workbenchType: true, // From details.notebooks.workbenchType
//         solutionOwner: true,
//         createdAt: true,
//     };
//     const [columnFilters, setColumnFilters] = useState<Record<string, boolean>>(initialColumnFilters);
//     const [tempColumnFilters, setTempColumnFilters] = useState<Record<string, boolean>>(initialColumnFilters);

//     useEffect(() => {
//         const fetchData = async () => {
//             setIsLoading(true);
//             try {
//                 const response = await queryGraphql(`
//                     query GetNotebooks {
//                       getNotebooks {
//                         id
//                         name
//                         solutionType
//                         costLabelPairs {
//                           cost
//                           label
//                         }
//                         status
//                         details {
//                           notebooks {
//                             region
//                             zone
//                             workbenchType
//                             jupyterLabVersion
//                             machineType
//                             network
//                             subNetwork
//                           }
//                         }
//                         reasonForRejection
//                         createdAt
//                         updatedAt
//                         solutionOwner {
//                           id
//                           name
//                         }
//                         solutionApprover {
//                           id
//                           name
//                         }
//                       }
//                     }
//                 `);

//                 const notebooksData = response.data?.getNotebooks || response.getNotebooks || [];

//                 if (notebooksData) {
//                     const processedNotebooks = notebooksData.map((nb: any): Notebook => ({
//                         id: nb.id,
//                         name: nb.name || "Unnamed Notebook",
//                         solutionType: nb.solutionType,
//                         costLabelPairs: nb.costLabelPairs,
//                         status: (nb.status || 'pending').toLowerCase(), // Ensure status is lowercase
//                         details: nb.details, // Keep the nested structure
//                         reasonForRejection: nb.reasonForRejection,
//                         createdAt: nb.createdAt,
//                         updatedAt: nb.updatedAt,
//                         solutionOwner: nb.solutionOwner ? {
//                             id: nb.solutionOwner.id,
//                             name: nb.solutionOwner.name,
//                         } : null,
//                         solutionApprover: nb.solutionApprover ? {
//                             id: nb.solutionApprover.id,
//                             name: nb.solutionApprover.name,
//                         } : null,
//                     }));
//                     setAllNotebooks(processedNotebooks);

//                     // Generate searchOptionsMasterList based on processedNotebooks
//                     let dynamicSearchOptions: SearchOptions[] = processedNotebooks
//                         .map((pn: Notebook) => [
//                             { category: "name", groupBy: "NOTEBOOK NAME", chipLabel: `Name: ${pn.name}`, dropdownLabel: pn.name },
//                             ...(pn.solutionType ? [{ category: "solutionType", groupBy: "SOLUTION TYPE", chipLabel: `Type: ${pn.solutionType}`, dropdownLabel: pn.solutionType }] : []),
//                             { category: "status", groupBy: "STATUS", chipLabel: `Status: ${pn.status}`, dropdownLabel: pn.status.charAt(0).toUpperCase() + pn.status.slice(1) },
//                             ...(pn.details?.notebooks?.region ? [{ category: "details.notebooks.region" as any, groupBy: "REGION", chipLabel: `Region: ${pn.details.notebooks.region}`, dropdownLabel: pn.details.notebooks.region }] : []),
//                             ...(pn.details?.notebooks?.machineType ? [{ category: "details.notebooks.machineType" as any, groupBy: "MACHINE TYPE", chipLabel: `Machine: ${pn.details.notebooks.machineType}`, dropdownLabel: pn.details.notebooks.machineType }] : []),
//                             ...(pn.details?.notebooks?.workbenchType ? [{ category: "details.notebooks.workbenchType" as any, groupBy: "WORKBENCH TYPE", chipLabel: `Workbench: ${pn.details.notebooks.workbenchType}`, dropdownLabel: pn.details.notebooks.workbenchType }] : []),
//                             ...(pn.solutionOwner ? [{ category: "solutionOwner.name" as any, groupBy: "OWNER", chipLabel: `Owner: ${pn.solutionOwner.name}`, dropdownLabel: pn.solutionOwner.name }] : []),
//                         ])
//                         .flat();

//                     dynamicSearchOptions = dynamicSearchOptions.filter(
//                         (obj, index, self) =>
//                             index === self.findIndex((t) =>
//                                 t.category === obj.category && t.dropdownLabel === obj.dropdownLabel
//                             )
//                     );
//                     dynamicSearchOptions.sort((a, b) => {
//                         const groupA = a.groupBy.toUpperCase();
//                         const groupB = b.groupBy.toUpperCase();
//                         if (groupA > groupB) return 1;
//                         if (groupA < groupB) return -1;
//                         const labelA = String(a.dropdownLabel).toUpperCase(); // Ensure dropdownLabel is string
//                         const labelB = String(b.dropdownLabel).toUpperCase(); // Ensure dropdownLabel is string
//                         if (labelA > labelB) return 1;
//                         if (labelA < labelB) return -1;
//                         return 0;
//                     });
//                     setSearchOptionsMasterList(dynamicSearchOptions);
//                 }
//             } catch (error) {
//                 console.error("Error fetching Notebooks:", error);
//             } finally {
//                 setIsLoading(false);
//             }
//         };
//         fetchData();
//     }, []);

//     const handleColumnFilterClick = (event: React.MouseEvent<HTMLElement>) => {
//         setTempColumnFilters(columnFilters);
//         setColumnFilterAnchorEl(event.currentTarget);
//     };

//     const handleColumnFilterClose = () => {
//         setColumnFilterAnchorEl(null);
//     };

//     const handleTempColumnFilterChange = (column: string) => {
//         setTempColumnFilters((prevFilters) => ({
//             ...prevFilters,
//             [column]: !prevFilters[column],
//         }));
//     };

//     const handleApplyColumnFilters = () => {
//         setColumnFilters(tempColumnFilters);
//         handleColumnFilterClose();
//     };

//     const getDisplayLabelForKey = (key: string): string => {
//         // Order matters for specific overrides
//         if (key === 'name') return 'Notebook Name';
//         if (key === 'solutionType') return 'Solution Type';
//         if (key === 'region') return 'Region';
//         if (key === 'machineType') return 'Machine Type';
//         if (key === 'workbenchType') return 'Workbench Type';
//         if (key === 'solutionOwner') return 'Solution Owner';
//         if (key === 'createdAt') return 'Added On';
        
//         // Generic camelCase to Title Case
//         const result = key.replace(/([A-Z])/g, ' $1');
//         return result.charAt(0).toUpperCase() + result.slice(1).trim();
//     };


//     const globallyFilteredNotebooks =
//         searchQuery.length === 0
//             ? allNotebooks
//             : allNotebooks.filter((notebook: Notebook) => {
//                 return searchQuery.every(queryParam => {
//                     let notebookValue: any;
//                     // Handle nested properties for filtering
//                     if (queryParam.category === "solutionOwner.name") {
//                         notebookValue = notebook.solutionOwner?.name;
//                     } else if (queryParam.category === "details.notebooks.region") {
//                         notebookValue = notebook.details?.notebooks?.region;
//                     } else if (queryParam.category === "details.notebooks.machineType") {
//                         notebookValue = notebook.details?.notebooks?.machineType;
//                     } else if (queryParam.category === "details.notebooks.workbenchType") {
//                         notebookValue = notebook.details?.notebooks?.workbenchType;
//                     } else {
//                         notebookValue = notebook[queryParam.category as keyof Notebook];
//                     }

//                     if (notebookValue === null || notebookValue === undefined) return false;
//                     return String(notebookValue).toLowerCase().includes(String(queryParam.dropdownLabel).toLowerCase());
//                 });
//             });
    
//     // Statuses from your API: 'pending', 'active', 'rejected', 'approved', 'provisioning', 'failed', 'success', 'submitted'
//     // Grouping them for accordions:
//     const pendingNotebooks = globallyFilteredNotebooks.filter(nb => nb.status === 'pending' || nb.status === 'submitted' || nb.status === 'provisioning');
//     const activeNotebooks = globallyFilteredNotebooks.filter(nb => nb.status === 'active' || nb.status === 'approved' || nb.status === 'success');
//     const rejectedNotebooks = globallyFilteredNotebooks.filter(nb => nb.status === 'rejected' || nb.status === 'failed');

//     const totalNotebooksOverall = allNotebooks.length;
//     const totalMatchingSearch = globallyFilteredNotebooks.length;

//     // Assuming user roles for adding new notebook is similar to AI Agent
//     const canAddNotebook = user.roles.includes("cluster_owner") || user.roles.includes("viewer");


//     const handleAddNotebook = () => {
//         navigate("/aisolutions/notebooks/add"); // Adjusted path
//     };

//     if (isLoading) {
//         return (
//             <Box sx={{ display: "flex" }}>
//                 <MainDrawer index={"aisolutions-notebooks"} /> {/* Adjusted index */}
//                 <Box component="main" sx={{ flex: 1, p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', height: 'calc(100vh - 64px)' }}>
//                     <CircularProgress />
//                 </Box>
//             </Box>
//         );
//     }

//     return (
//         <Box sx={{ display: "flex" }}>
//             <MainDrawer index={"aisolutions-notebooks"} /> {/* Adjusted index */}
//             <Box
//                 component="main"
//                 sx={{
//                     flex: 1,
//                     bgcolor: "#fafafa",
//                     minHeight: "100vh",
//                 }}
//             >
//                 <MiniBot />
//                 <Container maxWidth={false} disableGutters>
//                     <Box bgcolor="#FFFFFF" p={{xs:1.5, sm:2}} mb="18px" >
//                         <Box
//                             sx={{
//                                 display: "flex",
//                                 flexDirection: { xs: 'column', sm: 'row' },
//                                 justifyContent: "space-between",
//                                 alignItems: { xs: 'flex-start', sm: 'center' },
//                                  mx:2
//                             }}
//                         >
//                             <Typography fontSize="22px" gutterBottom fontFamily="Gilroy-Bold" color="#1F2937">
//                                 Notebooks {/* Changed Title */}
//                             </Typography>
//                             <Box sx={{display:'flex', alignItems:'center'}}> {/* Ensured IconButton is aligned */}
//                                 <Box>
//                                     {canAddNotebook && (
//                                         <Button
//                                             variant="contained"
//                                             sx={{
//                                                 backgroundColor: "black", color: "white", padding: "8px 24px",
//                                                 fontSize: "0.875rem", textTransform: "none", fontFamily: "Gilroy-Bold",
//                                                 borderRadius: "6px", '&:hover': { backgroundColor: '#333333' }
//                                             }}
//                                             onClick={handleAddNotebook}
//                                             // Disabled logic maintained from original, adjust if Notebook permissions differ
//                                             disabled={!user.roles.includes("cluster_owner") && !user.roles.includes("viewer")}
//                                         >
//                                             Add New Notebook {/* Changed Button Text */}
//                                         </Button>
//                                     )}
//                                 </Box>
//                                 <IconButton
//                                     aria-label="Help" 
//                                     onClick={()=> window.open(`${process.env.REACT_APP_HOST_DOMAIN}/docs` , "_blank")}
//                                     sx={{
//                                         padding: 0, 
//                                         border: 'none',  
//                                         ml:2,
//                                         '&:hover': {
//                                             backgroundColor: 'action.hover', 
//                                         },
//                                     }}
//                                 >
//                                     <Box
//                                         component="img"
//                                         src="/assets/Help.svg" // Generic icon, kept as is
//                                         alt="Help" 
//                                         sx={{
//                                             width: 40,  
//                                             height: 40,
//                                             display: 'block', 
//                                         }}
//                                     />
//                                 </IconButton>
//                             </Box>
//                         </Box>
//                         <Divider sx={{ marginY: 2 }} />
//                         <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: 'wrap' }}>
//                             <Autocomplete
//                                 multiple
//                                 options={searchOptionsMasterList}
//                                 filterSelectedOptions
//                                 getOptionLabel={(option) => option.dropdownLabel}
//                                 value={searchQuery}
//                                 groupBy={(option) => option.groupBy}
//                                 renderTags={(tagValue, getTagProps) =>
//                                     tagValue.map((option, index) => (
//                                         <Chip {...getTagProps({ index })} size="small" label={option.chipLabel} sx={{ fontFamily: "Gilroy-Regular" }} />
//                                     ))
//                                 }
//                                 onChange={(e, newVal) => {
//                                     setSearchQuery(newVal as SearchOptions[]);
//                                 }}
//                                 renderInput={(params) => (
//                                     <TextField
//                                         {...params}
//                                         variant="filled"
//                                         placeholder="Search by notebook name, type, status, etc." // Adjusted placeholder
//                                         fullWidth
//                                         size="small"
//                                         hiddenLabel
//                                         sx={{ minWidth: { xs: '100%', sm: 300, md: 500 }, flexGrow: 1 }}
//                                         InputProps={{
//                                             ...params.InputProps,
//                                             startAdornment: (
//                                                 <>
//                                                     <InputAdornment position="start" sx={{ pl: 0.5 }}>
//                                                         <img src="/assets/search-normal.svg" alt="Search Icon" style={{ width: "16px" }} />
//                                                     </InputAdornment>
//                                                     {params.InputProps.startAdornment}
//                                                 </>
//                                             ),
//                                             disableUnderline: true,
//                                             style: { backgroundColor: "#f4f4f4", minHeight: "44px", borderRadius: "8px", fontFamily: "Gilroy-Regular", fontSize: "14px" },
//                                         }}
//                                     />
//                                 )}
//                                 PaperComponent={(props) => <Paper elevation={3} {...props} />}
//                             />
//                             <Button
//                                 sx={{
//                                     border: "1px solid #bbb",
//                                     borderRadius: "10px",
//                                     height: "44px",
//                                     minWidth: "44px",
//                                     padding: "0px",
//                                     color: "#4B5563",
//                                     '&:hover': { backgroundColor: "#E5E7EB", borderColor: "#9CA3AF" }
//                                 }}
//                                 onClick={handleColumnFilterClick}
//                                 title="Filter Columns"
//                             >
//                                 <img src="/assets/pause.svg" alt="Filter Columns" style={{ width: "20px", height: "20px" }} />
//                             </Button>
//                             <Menu
//                                 anchorEl={columnFilterAnchorEl}
//                                 open={Boolean(columnFilterAnchorEl)}
//                                 onClose={handleColumnFilterClose}
//                                 anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
//                                 transformOrigin={{ vertical: "top", horizontal: "right" }}
//                                 PaperProps={{ sx: { border: '1px solid #E0E0E0', borderRadius: '8px', boxShadow: '0px 4px 12px rgba(0,0,0,0.1)', mt: 1 } }}
//                             >
//                                 <Box sx={{ p: "16px", minWidth: 240, maxWidth: 280 }}>
//                                     <Typography sx={{ fontFamily: "Gilroy-Bold", fontSize: "16px", fontWeight: 700, color: "#1F2937", marginBottom: "16px" }}>
//                                         TABLE COLUMNS
//                                     </Typography>
//                                     <FormGroup>
//                                         {Object.entries(tempColumnFilters).map(([key, value]) => (
//                                             <FormControlLabel
//                                                 key={key}
//                                                 control={
//                                                     <Checkbox
//                                                         checked={value}
//                                                         onChange={() => handleTempColumnFilterChange(key)}
//                                                         size="small"
//                                                         sx={{ color: "#4B5563", "&.Mui-checked": { color: "#000000" }, "& .MuiSvgIcon-root": { fontSize: "22px" }, py: '4px', mr: '8px' }}
//                                                     />
//                                                 }
//                                                 label={<Typography sx={{ fontFamily: "Gilroy-Medium", fontSize: '17px', color: '#1F2937', lineHeight: '1.5', py: '1vh' }}>{getDisplayLabelForKey(key)}</Typography>}
//                                                 sx={{ marginBottom: '4px' }}
//                                             />
//                                         ))}
//                                     </FormGroup>
//                                     <Button
//                                         variant="contained" fullWidth onClick={handleApplyColumnFilters}
//                                         sx={{ fontFamily: "Gilroy-Bold", fontSize: "14px", fontWeight: 700, color: "#FFFFFF", backgroundColor: "#000000", textTransform: 'uppercase', borderRadius: "6px", padding: "10px 0", marginTop: "20px", '&:hover': { backgroundColor: "#333333" } }}
//                                     >
//                                         Apply
//                                     </Button>
//                                 </Box>
//                             </Menu>
//                         </Box>
//                     </Box>

//                     <Box m={{xs: 1, sm:2}}>
//                         {totalNotebooksOverall > 0 && totalMatchingSearch > 0 &&(
//                             <>
//                         {/* Pending Accordion */}
//                         <Accordion
//                             defaultExpanded={pendingNotebooks.length > 0}
//                             elevation={0}
//                             sx={{ borderRadius: "8px", mb: "16px", "&:before": { display: "none" }, bgcolor: "#fafafa" }}
//                             disabled={pendingNotebooks.length === 0 && searchQuery.length > 0}
//                         >
//                             <AccordionSummary expandIcon={<ExpandMoreIcon />}>
//                                 <Box sx={{ display: "flex", alignItems: "center" }}>
//                                     <img src="/assets/clock.svg" alt="Pending Icon" style={{ width: "20px", marginRight: "8px" }} />
//                                     <Typography fontSize="16px" fontFamily="Gilroy-SemiBold">
//                                         Pending Notebooks ({pendingNotebooks.length}) {/* Adjusted Title */}
//                                     </Typography>
//                                 </Box>
//                             </AccordionSummary>
//                             <AccordionDetails sx={{ p: { xs: 1, sm: 2 } }}>
//                                 {pendingNotebooks.length > 0 ? (
//                                     <NotebooksTable
//                                         notebooks={pendingNotebooks}
//                                         columnFilters={columnFilters}
//                                         statusType="pending" // Or a more specific status if needed for styling
//                                     />
//                                 ) : (
//                                     <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', fontFamily:"Gilroy-Regular", py:2 }}>
//                                         No pending notebooks match your filters.
//                                     </Typography>
//                                 )}
//                             </AccordionDetails>
//                         </Accordion>

//                         {/* Active Accordion */}
//                         <Accordion
//                             defaultExpanded={activeNotebooks.length > 0}
//                             elevation={0}
//                             sx={{ borderRadius: "8px", mb: "16px", "&:before": { display: "none" }, bgcolor: "#fafafa" }}
//                             disabled={activeNotebooks.length === 0 && searchQuery.length > 0}
//                         >
//                             <AccordionSummary expandIcon={<ExpandMoreIcon />}>
//                                 <Box sx={{ display: "flex", alignItems: "center" }}>
//                                     <img src="/assets/tickcircle.svg" alt="Active Icon" style={{ width: "20px", marginRight: "8px" }} />
//                                     <Typography fontSize="16px" fontFamily="Gilroy-SemiBold">
//                                         Active Notebooks ({activeNotebooks.length}) {/* Adjusted Title */}
//                                     </Typography>
//                                 </Box>
//                             </AccordionSummary>
//                             <AccordionDetails sx={{ p: { xs: 1, sm: 2 } }}>
//                                 {activeNotebooks.length > 0 ? (
//                                     <NotebooksTable
//                                         notebooks={activeNotebooks}
//                                         columnFilters={columnFilters}
//                                         statusType="active" // Or a more specific status
//                                     />
//                                 ) : (
//                                      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', fontFamily:"Gilroy-Regular", py:2 }}>
//                                         No active notebooks match your filters.
//                                     </Typography>
//                                 )}
//                             </AccordionDetails>
//                         </Accordion>

//                         {/* Rejected Accordion */}
//                         <Accordion
//                             defaultExpanded={rejectedNotebooks.length > 0}
//                             elevation={0}
//                             sx={{ borderRadius: "8px", "&:before": { display: "none" }, bgcolor: "#fafafa" }}
//                             disabled={rejectedNotebooks.length === 0 && searchQuery.length > 0}
//                         >
//                             <AccordionSummary expandIcon={<ExpandMoreIcon />}>
//                                 <Box sx={{ display: "flex", alignItems: "center" }}>
//                                     <img src="/assets/reject.svg" alt="Rejected Icon" style={{ width: "20px", marginRight: "8px" }} />
//                                     <Typography fontSize="16px" fontFamily="Gilroy-SemiBold">
//                                         Rejected Notebooks ({rejectedNotebooks.length}) {/* Adjusted Title */}
//                                     </Typography>
//                                 </Box>
//                             </AccordionSummary>
//                             <AccordionDetails sx={{ p: { xs: 1, sm: 2 } }}>
//                                 {rejectedNotebooks.length > 0 ? (
//                                     <NotebooksTable
//                                         notebooks={rejectedNotebooks}
//                                         columnFilters={columnFilters}
//                                         statusType="rejected" // Or a more specific status
//                                     />
//                                 ) : (
//                                      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', fontFamily:"Gilroy-Regular", py:2 }}>
//                                         No rejected notebooks match your filters.
//                                     </Typography>
//                                 )}
//                             </AccordionDetails>
//                         </Accordion>
//                         </>)}
//                          {totalNotebooksOverall > 0 && totalMatchingSearch === 0 && !isLoading && (
//                             <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" sx={{ textAlign: 'center', mt: 5, p: 2, backgroundColor:"#FFFFFF", borderRadius:"8px" }}>
//                                 <img src="/assets/inbox-icon.svg" alt="No Notebooks Match" style={{ width: '100px', marginBottom: '20px' }} />
//                                 <Typography variant="h6" sx={{ mb: 1, fontFamily: "Gilroy-SemiBold", color: "#1F2937" }}>
//                                     No Notebooks match your search
//                                 </Typography>
//                                 <Typography variant="body2" color="#6B7280" sx={{ mb: 3, fontFamily: "Gilroy-Regular", maxWidth: '350px' }}>
//                                     Try adjusting your search terms or filters.
//                                 </Typography>
//                             </Box>
//                         )}

//                         {totalNotebooksOverall === 0 && !isLoading && (
//                              <Box
//                                 display="flex" flexDirection="column" justifyContent="center" alignItems="center"
//                                 sx={{
//                                     textAlign: 'center', mt: { xs: 2, sm: 5 }, px: 2, py: 4,
//                                     backgroundColor: '#FFFFFF', borderRadius: "8px",
//                                     boxShadow: "0px 1px 3px rgba(0,0,0,0.05)"
//                                 }}
//                             >
//                                 <img src="/assets/inbox-icon.svg" alt="No Notebooks" style={{ width: '100px', marginBottom: '20px' }} />
//                                 <Typography variant="h6" sx={{ mb: 1, fontFamily: "Gilroy-SemiBold", color: "#1F2937" }}>
//                                     No Notebooks are added yet
//                                 </Typography>
//                                 <Typography variant="body2" color="#6B7280" sx={{ mb: 3, fontFamily: "Gilroy-Regular", maxWidth: '380px' }}>
//                                     Click the button above to add your first Notebook.
//                                 </Typography>
//                             </Box>
//                         )}
//                     </Box>
//                 </Container>
//             </Box>
//         </Box>
//     );
// };

// export default Notebooks;